import { buildComposerDraftKey } from "./draftKey";
import { draftRepository, DraftStorageError } from "./draftRepository";
import type { ComposerDraftScope } from "./types";

const USER_PURGE_PREFIX = "dolphin.composer-draft-cleanup.v1.user.";
const DRAFT_REMOVAL_PREFIX = "dolphin.composer-draft-cleanup.v1.draft.";

export interface PendingDraftCleanupObligations {
  userIds: number[];
  draftKeys: string[];
}

const getStorage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

const listStorageKeys = (storage: Storage): string[] => {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key) keys.push(key);
  }
  return keys;
};

const userPurgeStorageKey = (userId: number): string =>
  `${USER_PURGE_PREFIX}${userId}`;

const draftRemovalStorageKey = (draftKey: string): string =>
  `${DRAFT_REMOVAL_PREFIX}${encodeURIComponent(draftKey)}`;

const persistObligation = (key: string): void => {
  try {
    getStorage()?.setItem(key, "1");
  } catch {
    // Cleanup is also attempted immediately. Storage failure must not block it.
  }
};

const completeObligation = (key: string): void => {
  try {
    getStorage()?.removeItem(key);
  } catch {
    // A later maintenance pass can retry if storage becomes available.
  }
};

const toStorageError = (error: unknown): DraftStorageError =>
  error instanceof DraftStorageError
    ? error
    : new DraftStorageError("unavailable", error);

export const recordPendingUserDraftPurge = (userId: number): void => {
  if (!Number.isSafeInteger(userId) || userId <= 0) return;
  persistObligation(userPurgeStorageKey(userId));
};

export const completePendingUserDraftPurge = (userId: number): void => {
  completeObligation(userPurgeStorageKey(userId));
};

export const recordPendingDraftRemoval = (scope: ComposerDraftScope): void => {
  persistObligation(draftRemovalStorageKey(buildComposerDraftKey(scope)));
};

export const completePendingDraftRemoval = (
  scope: ComposerDraftScope,
): void => {
  completeObligation(draftRemovalStorageKey(buildComposerDraftKey(scope)));
};

export const getPendingDraftCleanupObligations =
  (): PendingDraftCleanupObligations => {
    const storage = getStorage();
    if (!storage) return { userIds: [], draftKeys: [] };

    const userIds: number[] = [];
    const draftKeys: string[] = [];
    for (const key of listStorageKeys(storage)) {
      if (key.startsWith(USER_PURGE_PREFIX)) {
        const userId = Number(key.slice(USER_PURGE_PREFIX.length));
        if (Number.isSafeInteger(userId) && userId > 0) userIds.push(userId);
      } else if (key.startsWith(DRAFT_REMOVAL_PREFIX)) {
        try {
          draftKeys.push(
            decodeURIComponent(key.slice(DRAFT_REMOVAL_PREFIX.length)),
          );
        } catch {
          // Malformed application-owned identifiers are ignored safely.
        }
      }
    }

    return {
      userIds: [...new Set(userIds)].sort((left, right) => left - right),
      draftKeys: [...new Set(draftKeys)].sort(),
    };
  };

export const hasPendingDraftCleanupForScope = (
  scope: ComposerDraftScope,
): boolean => {
  const pending = getPendingDraftCleanupObligations();
  return (
    pending.userIds.includes(scope.userId) ||
    pending.draftKeys.includes(buildComposerDraftKey(scope))
  );
};

const retryUserPurge = async (
  userId: number,
): Promise<DraftStorageError | null> => {
  try {
    const result = await draftRepository.removeForUser(userId);
    if (result instanceof DraftStorageError) return result;
    completePendingUserDraftPurge(userId);
    return null;
  } catch (error) {
    return toStorageError(error);
  }
};

const retryDraftRemoval = async (
  draftKey: string,
): Promise<DraftStorageError | null> => {
  try {
    const result = await draftRepository.removeByKey(draftKey);
    if (result instanceof DraftStorageError) return result;
    completeObligation(draftRemovalStorageKey(draftKey));
    return null;
  } catch (error) {
    return toStorageError(error);
  }
};

export const retryPendingDraftCleanup = async (): Promise<void> => {
  const pending = getPendingDraftCleanupObligations();
  for (const userId of pending.userIds) await retryUserPurge(userId);
  for (const draftKey of pending.draftKeys) await retryDraftRemoval(draftKey);
};

export const retryPendingDraftCleanupForScope = async (
  scope: ComposerDraftScope,
): Promise<DraftStorageError | null> => {
  const pending = getPendingDraftCleanupObligations();
  let failure: DraftStorageError | null = null;
  if (pending.userIds.includes(scope.userId)) {
    failure = await retryUserPurge(scope.userId);
  }

  const draftKey = buildComposerDraftKey(scope);
  if (pending.draftKeys.includes(draftKey)) {
    const removalFailure = await retryDraftRemoval(draftKey);
    failure ??= removalFailure;
  }
  return failure;
};

export const clearPendingDraftCleanupObligations = (): void => {
  const storage = getStorage();
  if (!storage) return;
  for (const key of listStorageKeys(storage)) {
    if (
      key.startsWith(USER_PURGE_PREFIX) ||
      key.startsWith(DRAFT_REMOVAL_PREFIX)
    ) {
      completeObligation(key);
    }
  }
};
